import { useEffect, useState } from "react";
import { useMCPTT } from "../mcptt/hooks";
import { BRAND_TAGLINE, BRAND_TITLE } from "../config";

/**
 * Login screen — branded MCPTT auth card. Server is hard-coded in
 * config.ts; operator never sees it. Sign-in connects + logs in via
 * MCPTTProvider; on success the App router will swap us out.
 */
export function Login() {
  const { signIn, snapshot, enableAudio } = useMCPTT();
  const [user, setUser]         = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy]         = useState(false);
  const [localError, setLocal]  = useState<string | null>(null);
  const [rememberLogin, setRememberLogin] = useState(false);

  // Prefill the username on remount if we know it from a prior session.
  useEffect(() => {
    try {
      const last = window.localStorage.getItem("mcptt:lastLogin");
      if (last) {
        setUser(last);
        setRememberLogin(true);
      }
    } catch { /* private mode */ }
  }, []);

  const error = localError ?? snapshot.authError;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user.trim()) {
      setLocal("Login is required");
      return;
    }
    setLocal(null);
    setBusy(true);
    try {
      try {
        if (rememberLogin) window.localStorage.setItem("mcptt:lastLogin", user.trim());
        else window.localStorage.removeItem("mcptt:lastLogin");
      } catch { /* ignore */ }
      // Dispatcher console default: audio should be on. Start audio
      // immediately from the Sign In click path so the browser user
      // activation is still available for AudioContext/getUserMedia.
      // We do not fail login if the operator denies mic permission;
      // RX may still work and the app bar exposes the state.
      void enableAudio().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[mcptt] auto-enable audio failed", err);
      });
      await signIn(user.trim(), password);
    } catch (err) {
      // signIn rejects on auth-error too; surface message.
      const msg = err instanceof Error ? err.message : String(err);
      setLocal(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-brand">
        <div className="auth-logo" aria-hidden />
        <div className="auth-title">{BRAND_TITLE}</div>
        <div className="auth-tag">{BRAND_TAGLINE}</div>
      </div>

      <div className="auth-card">
        <form onSubmit={onSubmit} autoComplete="off">
          {/* Decoy fields absorb browser/password-manager heuristics.
              The real password input below is intentionally NOT
              type=password because Chrome ignores autocomplete flags
              on login-looking forms and still opens the password
              manager. We mask the real input with CSS instead. */}
          <input className="auth-decoy" type="text" name="username" autoComplete="username" tabIndex={-1} aria-hidden="true" />
          <input className="auth-decoy" type="password" name="password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" />
          <div className={`field ${error ? "error" : ""}`}>
            <label htmlFor="login">Login</label>
            <input
              id="login"
              name="mcptt-login"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              value={user}
              onChange={(e) => setUser(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className={`field ${error ? "error" : ""}`}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="mcptt-password"
              type="text"
              className="auth-password-mask"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </div>
          <label className="remember-login">
            <input
              type="checkbox"
              checked={rememberLogin}
              onChange={(e) => setRememberLogin(e.target.checked)}
              disabled={busy}
            />
            <span>Remember login name on this workstation</span>
          </label>
          <div className="auth-error">{error ?? "\u00A0"}</div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Signing In\u2026" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
